import { Box, CircularProgress, Stack } from "@mui/joy";
import React, { useEffect, useRef } from "react";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import Link from "../../components/common/Link";
import useGetUrls from "../../hooks/useGetUrls";
import GoogleLoginButtonPromotion from "../../components/user/GoogleLoginButtonPromotion";
import AppleLoginButtonPromotion from "../../components/user/AppleLoginButtonPromotion";
import KakaoLoginButtonPromotion from "../../components/user/KakaoLoginButtonPromotion";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import useMixpanelMKT from "../../hooks/useMixpanelMKT";
import useSafeAreaColor from "../../hooks/useSafeAreaColor";
import { useRecoilValue } from "recoil";
import { loginHintAtom } from "../../recoil/loginHintAtom";
import { getLoginButtonLayout } from "../../components/user/loginButtonLayout";
import CollapsibleLoginButtons from "../../components/user/CollapsibleLoginButtons";

import promotion1 from "../../static/image/promotion/login/1.png";
import promotion2 from "../../static/image/promotion/login/2.png";
import promotion3 from "../../static/image/promotion/login/3.png";
import promotion4 from "../../static/image/promotion/login/4.png";
import promotion5 from "../../static/image/promotion/login/5.png";
import promotion6 from "../../static/image/promotion/login/6.png";
import promotion7 from "../../static/image/promotion/login/7.png";
import promotion8 from "../../static/image/promotion/login/8.png";
import promotion9 from "../../static/image/promotion/login/9.png";
import promotion10 from "../../static/image/promotion/login/10.png";
import distancingLogo from "../../static/image/common/distancing_logo.png";
import dividerImg from "../../static/image/login/login_divider.svg";

const carouselItems = [
  { title: "매일 주어지는 활동지", image: promotion1 },
  { title: "이론 배우기", image: promotion2 },
  { title: "전담 코치의 리딩", image: promotion3 },
  { title: "패턴 발견하기", image: promotion4 },
  { title: "명상 훈련", image: promotion5 },
  { title: "생각 다루기", image: promotion6 },
  { title: "추적하며 관리하기", image: promotion7 },
  { title: "100일간의 여정", image: promotion8 },
  { title: "나 이해하기", image: promotion9 },
  { title: "가치 발견하기", image: promotion10 },
];

const CARD_WIDTH = 226;

// 레퍼럴 전용 독립 로그인 페이지.
// 기존 LoginPromotion 과 레이아웃은 동일하나 타이틀만 다르고,
// 로그인 버튼에 "최근 로그인" 배지를 노출한다(LoginButtonTemplatePromotion).
export default function ReferralLoginPage() {
  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();
  const { isChecking } = useAutoLoginPromotion();
  // 제휴 퍼널은 웹 전용 → 항상 접힘 대상 (Figma 8529-15562 1행)
  const loginHint = useRecoilValue(loginHintAtom);
  const loginLayout = getLoginButtonLayout("web", loginHint);

  const backgroundColor = theme.vars.palette.Background.Level2;
  useSafeAreaColor(backgroundColor);

  const trackEntry = useMixpanelMKT({
    eventName: "로그인 페이지 진입",
    params: { type: "affiliate" },
    version: "v4",
  });
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isChecking) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: backgroundColor,
        }}
      >
        <CircularProgress color="neutral" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        zIndex: 1000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "calc(var(--vh,1vh) * 100)",
        backgroundColor: backgroundColor,
        overflowY: "auto",
        display: "flex",
        justifyContent: "center",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "none",
      }}
    >
      <Stack
        direction={"column"}
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "660px",
          minHeight: "100%",
          pb: "20px",
          position: "relative",
        }}
      >
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="flex-start"
          spacing={4}
          sx={{ width: "100%", boxSizing: "border-box", pt: 4 }}
        >
          <img
            src={distancingLogo}
            alt="distancing logo"
            draggable={false}
            style={{ width: "138px", height: "auto", flexShrink: 0 }}
          />

          <Box
            sx={{
              width: "100%",
              maxWidth: "320px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: 2, sm: 4 },
              flexShrink: 0,
            }}
          >
            <Stack spacing={"12px"} sx={{ textAlign: "center", width: "100%" }}>
              <CustomTypography
                variant="H1"
                sx={{
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 800,
                  whiteSpace: "pre-wrap",
                }}
              >
                {"구독자 전용 우대 혜택과 함께 지금 시작하세요"}
              </CustomTypography>
              <CustomTypography
                variant="Subtitle"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                  whiteSpace: "pre-wrap",
                }}
              >
                {"효과가 입증된 비약물 심리치료"}
              </CustomTypography>
            </Stack>

            <Stack spacing={"6px"} sx={{ width: "100%" }} alignSelf="center">
              <CollapsibleLoginButtons
                layout={loginLayout}
                buttonByMethod={{
                  kakao: <KakaoLoginButtonPromotion />,
                  apple: <AppleLoginButtonPromotion />,
                  google: <GoogleLoginButtonPromotion />,
                }}
              />
            </Stack>
          </Box>

          <img
            src={dividerImg}
            alt={"empty"}
            style={{ width: "100%", maxWidth: "320px" }}
          />

          <Box
            sx={{
              width: "100%",
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              gap: "12px",
              px: `calc(50% - ${CARD_WIDTH / 2}px)`,
              boxSizing: "border-box",
              "::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {carouselItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: `0 0 ${CARD_WIDTH}px`,
                  scrollSnapAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <CustomTypography
                  variant="Body"
                  sx={{
                    py: 0,
                    color: theme.vars.palette.Content.Medium,
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  {item.title}
                </CustomTypography>
                <Box
                  sx={{
                    width: "100%",
                    height: "400px",
                    borderRadius: "24px",
                    overflow: "hidden",
                    border: `1px solid ${theme.vars.palette.Border.Weak}`,
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    draggable={false}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Stack>

        <Stack
          direction={"row"}
          spacing={"20px"}
          justifyContent="center"
          sx={{
            mt: "auto",
            mb: "40px",
            pb: "10px",
            pt: 4,
            wordBreak: "keep-all",
            flexShrink: 0,
          }}
        >
          <Link
            opacity={100}
            bold={false}
            text={"사업자 정보"}
            url={companyInfoUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"개인정보 처리방침"}
            url={privacyPolicyUrl}
          />
          <Link
            opacity={100}
            bold={false}
            text={"서비스 이용약관"}
            url={servicePolicyUrl}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
